// Services/MeshyGLBDownloadService.js - Complete Enhanced Version
import { auth, functions } from '../Config/firebase.js';

class MeshyGLBDownloadService {
  constructor() {
    console.log('📦 MeshyGLBDownloadService initialized with Cloud Functions');
  }

  // Download GLB using Cloud Function (bypasses CORS)
  async downloadGLBToFirebase(meshyUrl, itemName, itemId) {
    try {
      console.log('🔍 DEBUG: downloadGLBToFirebase called with:');
      console.log('  Raw parameters received:');
      console.log('    meshyUrl:', meshyUrl);
      console.log('    itemName:', itemName);
      console.log('    itemId:', itemId);
      console.log('  Parameter types:');
      console.log('    typeof meshyUrl:', typeof meshyUrl);
      console.log('    typeof itemName:', typeof itemName);
      console.log('    typeof itemId:', typeof itemId);
      console.log('  Parameter validation:');
      console.log('    meshyUrl exists:', !!meshyUrl);
      console.log('    meshyUrl has length:', meshyUrl?.length > 0);
      console.log('    itemName exists:', !!itemName);
      console.log('    itemId exists:', !!itemId);
      
      // Enhanced validation with detailed error messages
      if (!meshyUrl) {
        console.error('❌ VALIDATION FAILED: meshyUrl is falsy:', meshyUrl);
        throw new Error('meshyUrl is required but was falsy (null, undefined, or empty)');
      }
      
      if (typeof meshyUrl !== 'string') {
        console.error('❌ VALIDATION FAILED: meshyUrl is not a string:', typeof meshyUrl);
        throw new Error(`meshyUrl must be a string, got ${typeof meshyUrl}`);
      }
      
      if (!meshyUrl.trim()) {
        console.error('❌ VALIDATION FAILED: meshyUrl is empty string');
        throw new Error('meshyUrl cannot be empty');
      }
      
      if (!itemName || typeof itemName !== 'string' || !itemName.trim()) {
        console.error('❌ VALIDATION FAILED: invalid itemName:', itemName);
        throw new Error('itemName is required and must be a non-empty string');
      }
      
      if (!itemId || typeof itemId !== 'string' || !itemId.trim()) {
        console.error('❌ VALIDATION FAILED: invalid itemId:', itemId);
        throw new Error('itemId is required and must be a non-empty string');
      }
      
      console.log(`📥 Downloading GLB via Cloud Function: ${itemName}`);
      console.log(`🔗 From: ${meshyUrl.substring(0, 150)}...`);
      
      // Prepare the payload
      const payload = {
        meshyUrl: meshyUrl,
        itemName: itemName,
        itemId: itemId,
        fileType: 'glb'
      };
      
      console.log('📤 Sending to cloud function:');
      console.log('  Payload keys:', Object.keys(payload));
      console.log('  Payload.meshyUrl length:', payload.meshyUrl.length);
      console.log('  Payload.itemName:', payload.itemName);
      console.log('  Payload.itemId:', payload.itemId);
      
      // Call your Cloud Function (compat syntax)
      const downloadMeshyFile = functions.httpsCallable('downloadMeshyFile');
      
      const result = await downloadMeshyFile(payload);
      
      console.log(`✅ Cloud Function response:`, result.data);
      
      return {
        success: true,
        firebaseUrl: result.data.firebaseUrl,
        filename: result.data.filename,
        size: result.data.size
      };
      
    } catch (error) {
      console.error(`❌ Cloud Function failed for ${itemName}:`, error);
      console.error('❌ Error details:');
      console.error('  Error message:', error.message);
      console.error('  Error code:', error.code);
      console.error('  Error details:', error.details);
      console.error('  Full error:', error);
      
      return {
        success: false,
        error: error.message,
        originalUrl: meshyUrl
      };
    }
  }

  // Download image using Cloud Function
  async downloadImageToFirebase(meshyUrl, itemName, itemId) {
    try {
      console.log('🔍 DEBUG: downloadImageToFirebase called with:');
      console.log('  Raw parameters received:');
      console.log('    meshyUrl:', meshyUrl);
      console.log('    itemName:', itemName);
      console.log('    itemId:', itemId);
      
      // Same validation as GLB
      if (!meshyUrl || typeof meshyUrl !== 'string' || !meshyUrl.trim()) {
        console.error('❌ VALIDATION FAILED: invalid meshyUrl for image:', meshyUrl);
        throw new Error('meshyUrl is required and must be a non-empty string');
      }
      
      if (!itemName || typeof itemName !== 'string' || !itemName.trim()) {
        console.error('❌ VALIDATION FAILED: invalid itemName for image:', itemName);
        throw new Error('itemName is required and must be a non-empty string');
      }
      
      if (!itemId || typeof itemId !== 'string' || !itemId.trim()) {
        console.error('❌ VALIDATION FAILED: invalid itemId for image:', itemId);
        throw new Error('itemId is required and must be a non-empty string');
      }
      
      console.log(`📸 Downloading image via Cloud Function: ${itemName}`);
      
      const downloadMeshyFile = functions.httpsCallable('downloadMeshyFile');
      
      const result = await downloadMeshyFile({
        meshyUrl: meshyUrl,
        itemName: itemName,
        itemId: itemId,
        fileType: 'image'
      });
      
      console.log(`✅ Image Cloud Function response:`, result.data);
      
      return {
        success: true,
        firebaseUrl: result.data.firebaseUrl,
        filename: result.data.filename,
        size: result.data.size
      };
      
    } catch (error) {
      console.error(`❌ Image Cloud Function failed for ${itemName}:`, error);
      return {
        success: false,
        error: error.message,
        originalUrl: meshyUrl
      };
    }
  }

  // Download both GLB and image for a single item
  async downloadItemFiles(item) {
    console.log(`\n🔄 Processing item: ${item.itemName} (ID: ${item.id})`);
    
    const results = {
      itemId: item.id,
      itemName: item.itemName,
      glb: null,
      image: null,
      success: false
    };
    
    try {
      // Check what needs to be downloaded
      const needsGLBMigration = item.glbUrl && item.glbUrl.includes('meshy.ai');
      const needsImageMigration = item.imageUrl && item.imageUrl.includes('meshy.ai');
      
      if (!needsGLBMigration && !needsImageMigration) {
        console.log(`⏭️ Skipping ${item.itemName} - no Meshy URLs found`);
        results.success = true;
        return results;
      }
      
      // Download GLB if needed
      if (needsGLBMigration) {
        console.log(`📦 Downloading GLB for ${item.itemName}...`);
        results.glb = await this.downloadGLBToFirebase(item.glbUrl, item.itemName, item.id);
      }
      
      // Download image if needed
      if (needsImageMigration) {
        console.log(`🖼️ Downloading image for ${item.itemName}...`);
        results.image = await this.downloadImageToFirebase(item.imageUrl, item.itemName, item.id);
      }
      
      // Check if at least one download succeeded
      const glbOk = !needsGLBMigration || (results.glb && results.glb.success);
      const imageOk = !needsImageMigration || (results.image && results.image.success);
      
      results.success = glbOk && imageOk;
      
      if (results.success) {
        console.log(`✅ Successfully processed ${item.itemName}`);
      } else {
        console.log(`⚠️ Partial success for ${item.itemName}`);
      }
      
      return results;
      
    } catch (error) {
      console.error(`❌ Failed to process ${item.itemName}:`, error);
      results.error = error.message;
      return results;
    }
  }

  // Batch download multiple files using Cloud Function
  async batchDownloadFiles(items) {
    try {
      console.log(`📦 Batch downloading ${items.length} items via Cloud Function`);
      
      const files = [];
      
      // Prepare file list
      items.forEach(item => {
        if (item.glbUrl && item.glbUrl.includes('meshy.ai')) {
          files.push({
            meshyUrl: item.glbUrl,
            itemName: item.itemName,
            itemId: item.id,
            fileType: 'glb'
          });
        }
        if (item.imageUrl && item.imageUrl.includes('meshy.ai')) {
          files.push({
            meshyUrl: item.imageUrl,
            itemName: item.itemName,
            itemId: item.id,
            fileType: 'image'
          });
        }
      });
      
      if (files.length === 0) {
        console.log('✅ No Meshy files to download');
        return { success: true, results: [], summary: { total: 0, successful: 0, failed: 0 } };
      }
      
      console.log(`🚀 Calling batch download for ${files.length} files`);
      
      const downloadMeshyFiles = functions.httpsCallable('downloadMeshyFiles');
      const result = await downloadMeshyFiles({ files });
      
      console.log(`✅ Batch download complete:`, result.data.summary);
      return result.data;
      
    } catch (error) {
      console.error('❌ Batch download failed:', error);
      throw error;
    }
  }

  // Enhanced debugging for finding items
  findMeshyItems(stores) {
    console.log('🔍 DEBUG: findMeshyItems called with:');
    console.log('  stores type:', typeof stores);
    console.log('  stores length:', stores?.length || 0);
    
    const meshyItems = [];
    
    // Safety check for stores
    if (!stores || !Array.isArray(stores)) {
      console.warn('⚠️ No stores provided or stores is not an array');
      console.log('  stores value:', stores);
      return meshyItems;
    }
    
    stores.forEach((store, storeIndex) => {
      console.log(`🏪 Processing store ${storeIndex}: ${store?.name || 'Unknown'}`);
      
      // Safety check for store
      if (!store || !store.items || !Array.isArray(store.items)) {
        console.warn(`⚠️ Store ${store?.id || 'unknown'} has no items or items is not an array`);
        console.log(`  store.items:`, store?.items);
        return;
      }
      
      store.items.forEach((item, itemIndex) => {
        console.log(`  📦 Item ${itemIndex}: ${item?.itemName || 'Unknown'}`);
        console.log(`    glbUrl: ${item?.glbUrl || 'undefined'}`);
        console.log(`    imageUrl: ${item?.imageUrl || 'undefined'}`);
        console.log(`    id: ${item?.id || 'undefined'}`);
        
        // Safety check for item
        if (!item || !item.id) {
          console.warn('⚠️ Found item without ID, skipping');
          console.log('    item:', item);
          return;
        }
        
        const hasMeshyGLB = item.glbUrl && typeof item.glbUrl === 'string' && item.glbUrl.includes('meshy.ai');
        const hasMeshyImage = item.imageUrl && typeof item.imageUrl === 'string' && item.imageUrl.includes('meshy.ai');
        
        console.log(`    hasMeshyGLB: ${hasMeshyGLB}`);
        console.log(`    hasMeshyImage: ${hasMeshyImage}`);
        
        if (hasMeshyGLB || hasMeshyImage) {
          const meshyItem = {
            ...item,
            storeId: store.id || 'unknown',
            storeName: store.name || 'Unknown Store',
            needsGLBMigration: hasMeshyGLB,
            needsImageMigration: hasMeshyImage
          };
          
          console.log(`    ✅ Adding to migration list:`, {
            itemName: meshyItem.itemName,
            glbUrl: meshyItem.glbUrl,
            imageUrl: meshyItem.imageUrl,
            id: meshyItem.id
          });
          
          meshyItems.push(meshyItem);
        }
      });
    });
    
    console.log(`📊 Found ${meshyItems.length} items with Meshy URLs`);
    
    if (meshyItems.length > 0) {
      meshyItems.forEach((item, index) => {
        console.log(`  ${index + 1}. ${item.itemName || 'Unnamed Item'} (Store: ${item.storeName})`);
        console.log(`     GLB: ${item.needsGLBMigration ? '✅' : '❌'} - ${item.glbUrl || 'undefined'}`);
        console.log(`     Image: ${item.needsImageMigration ? '✅' : '❌'} - ${item.imageUrl || 'undefined'}`);
        console.log(`     ID: ${item.id || 'undefined'}`);
      });
    } else {
      console.log('  No Meshy URLs found in any items');
    }
    
    return meshyItems;
  }

  // Main migration function using Cloud Functions
  async migrateAllMeshyFiles(stores, onProgress = null, onItemComplete = null) {
    try {
      console.log('🚀 Starting migration of all Meshy files to Firebase using Cloud Functions...');
      
      // Find all Meshy items
      const meshyItems = this.findMeshyItems(stores);
      
      if (meshyItems.length === 0) {
        console.log('📋 No Meshy files found to migrate');
        return {
          success: true,
          totalItems: 0,
          migratedItems: 0,
          failedItems: 0,
          results: []
        };
      }
      
      // Use batch download for efficiency
      if (onProgress) {
        onProgress(10, `Starting batch download of ${meshyItems.length} items...`, 0, meshyItems.length);
      }
      
      const batchResult = await this.batchDownloadFiles(meshyItems);
      
      if (onProgress) {
        onProgress(100, 'Migration complete!', meshyItems.length, meshyItems.length);
      }
      
      const migrationResults = {
        success: batchResult.success,
        totalItems: batchResult.summary.total,
        migratedItems: batchResult.summary.successful,
        failedItems: batchResult.summary.failed,
        results: batchResult.results
      };
      
      console.log('📊 Migration Summary:');
      console.log(`  • Total items: ${migrationResults.totalItems}`);
      console.log(`  • Successfully migrated: ${migrationResults.migratedItems}`);
      console.log(`  • Failed: ${migrationResults.failedItems}`);
      
      // Call onItemComplete for each result if provided
      if (onItemComplete) {
        batchResult.results.forEach((result, index) => {
          onItemComplete(result, index + 1, batchResult.results.length);
        });
      }
      
      return migrationResults;
      
    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  // Update stores with new Firebase URLs
  updateStoresWithFirebaseUrls(stores, migrationResults) {
    console.log('🔄 Updating stores with Firebase URLs...');
    
    const updatedStores = stores.map(store => {
      const updatedItems = store.items.map(item => {
        // Find migration results for this item
        const glbResult = migrationResults.results.find(r => 
          r.fileType === 'glb' && r.itemName === item.itemName && r.success
        );
        const imageResult = migrationResults.results.find(r => 
          r.fileType === 'image' && r.itemName === item.itemName && r.success
        );
        
        if (!glbResult && !imageResult) return item;
        
        // Update URLs with Firebase URLs
        const updatedItem = { ...item };
        
        if (glbResult && glbResult.success) {
          updatedItem.glbUrl = glbResult.firebaseUrl;
        }
        
        if (imageResult && imageResult.success) {
          updatedItem.imageUrl = imageResult.firebaseUrl;
        }
        
        // Add migration metadata
        updatedItem.migratedToFirebase = true;
        updatedItem.migrationDate = new Date().toISOString();
        
        return updatedItem;
      });
      
      return { ...store, items: updatedItems };
    });
    
    console.log('✅ Stores updated with Firebase URLs');
    return updatedStores;
  }

  // Test function to manually verify a URL
  async testUrl(meshyUrl, itemName = 'test', itemId = 'test123') {
    console.log('🧪 Testing Meshy URL download...');
    console.log('URL:', meshyUrl);
    
    try {
      const result = await this.downloadGLBToFirebase(meshyUrl, itemName, itemId);
      console.log('✅ Test result:', result);
      return result;
    } catch (error) {
      console.error('❌ Test failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
const meshyDownloadService = new MeshyGLBDownloadService();
export default meshyDownloadService;